import React, { useState, useEffect } from "react";
import { FiX, FiPlusCircle, FiMinusCircle, FiUser } from "react-icons/fi";
import toast from "react-hot-toast";

const getUserId = (row) => row?.user_id ?? row?.userId ?? row?.userID ?? row?.id ?? null;

const AdjustPointsModal = ({ isOpen, onClose, onAdjust, participant = null }) => {
  const [points, setPoints] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("add"); // "add" | "deduct"
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPoints("");
    setNote("");
    setMode("add");
  }, [participant, isOpen]);

  if (!isOpen || !participant) return null;

  const rawUserId = getUserId(participant);
  const userIdStr = rawUserId !== undefined && rawUserId !== null ? String(rawUserId) : "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rawVal = parseFloat(points);
    if (isNaN(rawVal) || rawVal === 0) {
      toast.error("Please enter a valid points amount");
      return;
    }
    if (!note.trim()) {
      toast.error("Please provide a note/reason for audit logging");
      return;
    }
    if (!userIdStr) {
      toast.error("Participant user ID is missing");
      return;
    }

    const finalPoints = mode === "deduct" ? -Math.abs(rawVal) : Math.abs(rawVal);

    setLoading(true);
    try {
      await onAdjust({
        user_id: userIdStr,
        points: finalPoints,
        note: note.trim(),
      });
      toast.success(`Successfully adjusted points by ${finalPoints > 0 ? `+${finalPoints}` : finalPoints}`);
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to adjust points");
    } finally {
      setLoading(false);
    }
  };

  const currentTotal = Number(participant.total_points || 0);
  const enteredNum = parseFloat(points) || 0;
  const projectedTotal = mode === "deduct" ? currentTotal - enteredNum : currentTotal + enteredNum;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <FiUser size={16} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Adjust Participant Points</h2>
              <p className="text-xs text-gray-400">Manual point correction with audit ledger</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            <FiX size={17} />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* User Info Box */}
          <div className="p-3.5 bg-gray-50 rounded-xl border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-800">
                {participant.username || participant.email || "Unknown User"}
              </p>
              <p className="text-[11px] text-gray-400 font-mono truncate max-w-[200px]">{userIdStr}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Current Points</p>
              <p className="text-sm font-bold text-gray-800">{currentTotal.toLocaleString()}</p>
            </div>
          </div>

          {/* Mode Switcher */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Adjustment Action</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("add")}
                className={`py-2 px-3 rounded-lg text-xs font-medium border flex items-center justify-center gap-1.5 transition ${
                  mode === "add"
                    ? "bg-green-50 border-green-200 text-green-700 shadow-sm"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <FiPlusCircle size={14} /> Add Points (+)
              </button>
              <button
                type="button"
                onClick={() => setMode("deduct")}
                className={`py-2 px-3 rounded-lg text-xs font-medium border flex items-center justify-center gap-1.5 transition ${
                  mode === "deduct"
                    ? "bg-red-50 border-red-200 text-red-700 shadow-sm"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <FiMinusCircle size={14} /> Deduct Points (-)
              </button>
            </div>
          </div>

          {/* Points Input */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Points Amount <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0.01"
              step="any"
              placeholder="e.g. 50"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>

          {/* Projected Total */}
          {enteredNum > 0 && (
            <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100 flex items-center justify-between text-xs">
              <span className="text-blue-700 font-medium">Projected New Total:</span>
              <span className="text-blue-900 font-bold text-sm">
                {projectedTotal < 0 ? 0 : projectedTotal.toLocaleString()}
              </span>
            </div>
          )}

          {/* Reason / Note */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Reason / Note <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              placeholder="e.g. Correction for missed daily check-in on June 5th (admin ticket #123)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              required
            />
            <p className="text-[11px] text-gray-400 mt-1">This note is recorded permanently in the audit ledger</p>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className={`px-5 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition flex items-center gap-2 ${
              mode === "deduct"
                ? "bg-red-600 hover:bg-red-700 shadow-red-600/20"
                : "bg-green-600 hover:bg-green-700 shadow-green-600/20"
            }`}
          >
            {loading && (
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            Apply {mode === "deduct" ? "Deduction" : "Credit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustPointsModal;
